import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { ScrollArea } from "../components/ui/scroll-area";
import { ArrowRight } from "@phosphor-icons/react";
import { HeroScreenshot, BentoLayout, AsymmetricGrid, ScreenshotPair } from "./ScreenshotLayouts";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const subsections = [
  {
    id: "discovery",
    title: "Scalable Discovery Systems",
    teaser: "Category exploration, software comparisons, vendor showcases, multilingual workflows, and AI-assisted product search experiences.",
    image: "/screenshots/zoftware/zoft-homepage.png",
    narrative: [
      "Built and evolved scalable product discovery systems including category exploration, software comparisons, vendor showcases, multilingual workflows, AI-assisted discovery, and product search experiences.",
    ],
    tags: ["Category Systems", "Product Comparisons", "Vendor Showcases", "Multilingual Support", "Search UX"],
    layout: "hero+bento",
    hero: { src: "/screenshots/zoftware/zoft-homepage.png", alt: "ZoftwareHub Homepage", url: "zoftware.com", caption: "Software discovery platform — browse, compare, and evaluate enterprise tools across 600+ categories" },
    bento: [
      { src: "/screenshots/zoftware/zoft-categories.png", alt: "Browse All Categories", url: "zoftware.com/categories", caption: "600+ software categories with featured and trending highlights" },
      { src: "/screenshots/zoftware/zoft-comparison.png", caption: "Side-by-side product comparison across pricing, features, and ratings" },
      { src: "/screenshots/zoftware/zoft-product-detail.png", caption: "Product detail pages with pricing tiers and user reviews" },
    ],
  },
  {
    id: "transform",
    title: "Platform Transformation",
    teaser: "Migration from AngularJS to Next.js — SSR/CSR strategy, SEO optimization, performance tuning, and CI/CD modernization.",
    image: "/screenshots/zoftware/zoft-modern-ui.png",
    narrative: [
      "Led the migration from AngularJS toward a modern Next.js architecture after identifying major issues around poor SSR implementation, weak SEO visibility, slow page loads, frontend scalability issues, and developer experience limitations.",
      "The modernization effort focused on rendering strategy, SEO optimization, SSR/CSR balancing, frontend performance, caching, infrastructure redesign, and CI/CD improvements. This reduced page load times significantly while improving search discoverability and long-term scalability.",
    ],
    tags: ["SSR/CSR Strategy", "SEO Optimization", "Performance Tuning", "CI/CD Modernization", "Infrastructure Redesign"],
    layout: "pair",
    screenshots: [
      { src: "/screenshots/zoftware/zoft-modern-ui.png", alt: "Modernized Platform", url: "zoftware.com (modernized)", caption: "Modernized platform with Next.js SSR and optimized performance" },
      { src: "/screenshots/zoftware/zoft-category-detail.png", alt: "Category Detail Pages", url: "zoftware.com/categories/ai", caption: "SSR-rendered category pages for SEO discoverability" },
    ],
  },
  {
    id: "analytics",
    title: "Vendor Intelligence & Analytics",
    teaser: "Heatmaps, user journey tracking, lead intelligence, and engagement analytics for software vendors.",
    image: "/screenshots/zoftware/zoft-vendor-dashboard.png",
    narrative: [
      "Built operational analytics and monetization systems for software vendors, including heatmaps, user journey tracking, lead intelligence, engagement analytics, vendor dashboards, promotional systems, and operational visibility tooling.",
      "These systems helped vendors understand how users interacted with their products and improved discovery/conversion workflows across the platform.",
    ],
    tags: ["Heatmaps", "User Journey Tracking", "Lead Intelligence", "Engagement Analytics", "Vendor Dashboards"],
    layout: "hero+bento",
    hero: { src: "/screenshots/zoftware/zoft-vendor-dashboard.png", alt: "Vendor Analytics Dashboard", url: "zoftware.com/vendor/analytics", caption: "Vendor analytics dashboard — engagement tracking, lead intelligence, and conversion metrics" },
    bento: [
      { src: "/screenshots/zoftware/zoft-vendor-page.png", alt: "Vendor Profile", url: "zoftware.com/vendor/profile", caption: "Vendor profile and showcase pages" },
      { src: "/screenshots/zoftware/zoft-analytics.png", caption: "Engagement analytics and reporting systems" },
      { src: "/screenshots/zoftware/zoft-heatmap.png", caption: "User interaction heatmaps and journey tracking" },
    ],
  },
  {
    id: "ai",
    title: "AI Discovery",
    teaser: "AI Strategy Assistant, smart search with match scoring, conversational discovery, and automated RFP generation.",
    image: "/screenshots/zoftware/zoft-ai-assistant.png",
    narrative: [
      "Integrated conversational AI systems and voice-based discovery workflows that reduced friction across software exploration and recommendation experiences.",
      "Built an AI Strategy Assistant that walks users through domain-specific questions, diagnoses their tech maturity, identifies risks, and generates personalized strategy roadmaps with product recommendations and 90-day execution plans.",
      "Developed smart discovery with natural language search, budget/size filters, and AI-powered match scoring that returns ranked results with fit percentages — connected to an automated RFP builder for enterprise procurement workflows.",
    ],
    tags: ["AI Strategy Assistant", "Smart Discovery", "Match Scoring", "RFP Builder", "Conversational UI"],
    layout: "asymmetric",
    screenshots: [
      { src: "/screenshots/zoftware/zoft-ai-assistant.png", alt: "AI Strategy Assistant", caption: "AI Strategy Assistant — personalized questionnaire flow" },
      { src: "/screenshots/zoftware/zoft-strategy-preview.jpeg", alt: "Strategy Preview", caption: "AI-generated tech maturity diagnosis and strategic priorities" },
      { src: "/screenshots/zoftware/zoft-smart-discovery.png", alt: "Smart Discovery", caption: "Natural language search with budget and size filters" },
      { src: "/screenshots/zoftware/zoft-ai-results.png", alt: "AI Match Results", caption: "Ranked results with AI match percentages and RFP builder" },
      { src: "/screenshots/zoftware/zoft-rfp-builder.png", alt: "RFP Builder", caption: "Automated RFP generation from search criteria" },
    ],
  },
];

const ScreenshotRenderer = ({ data }) => (
  <>
    {data.layout === "hero+bento" && (
      <>
        <HeroScreenshot src={data.hero.src} alt={data.hero.alt} url={data.hero.url} caption={data.hero.caption} />
        <BentoLayout images={data.bento} />
      </>
    )}
    {data.layout === "asymmetric" && <AsymmetricGrid images={data.screenshots} />}
    {data.layout === "pair" && <ScreenshotPair images={data.screenshots} />}
  </>
);

const SubsectionCard = ({ item, onClick }) => (
  <motion.button
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    onClick={onClick}
    className="text-left bg-[#121212] border border-white/[0.08] rounded-xl overflow-hidden hover:border-white/[0.18] transition-all duration-300 group"
    data-testid={`zoftware-card-${item.id}`}
  >
    <div className="aspect-[16/9] overflow-hidden bg-[#1C1C1C]">
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
      />
    </div>
    <div className="p-5">
      <h3 className="font-heading text-base font-semibold text-[#EDEDED] tracking-tight mb-2 group-hover:text-white transition-colors">
        {item.title}
      </h3>
      <p className="text-[13px] text-[#A1A1AA]/70 leading-relaxed line-clamp-2 mb-3">
        {item.teaser}
      </p>
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#38bdf8] group-hover:gap-2 transition-all">
        View details
        <ArrowRight size={12} weight="bold" />
      </span>
    </div>
  </motion.button>
);

export const CaseStudyZoftware = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [openSheet, setOpenSheet] = useState(null);

  const activeItem = subsections.find((s) => s.id === openSheet);

  return (
    <div ref={ref} className="mb-24" data-testid="case-study-zoftware">
      <motion.div
        initial="initial"
        animate={isInView ? "animate" : "initial"}
        transition={{ staggerChildren: 0.1 }}
        className="mb-10"
      >
        <motion.div
          variants={fadeUp}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 mb-5"
        >
          <span className="text-xs font-mono text-[#38bdf8] tracking-wider uppercase">Case Study 02</span>
          <span className="flex-1 h-px bg-white/[0.06]" />
        </motion.div>

        <motion.h2
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          data-testid="zoftware-heading"
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EDEDED] tracking-tight mb-3"
        >
          ZoftwareHub
        </motion.h2>

        <motion.p
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-base sm:text-lg text-[#A1A1AA] max-w-3xl leading-relaxed mb-4"
        >
          Scaling a software discovery marketplace for the MENA region.
        </motion.p>

        <motion.p
          variants={fadeUp}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="text-sm text-[#A1A1AA]/70 max-w-3xl leading-relaxed"
        >
          A software discovery and vendor intelligence platform helping enterprises discover products, vendors, and system integrators. Evolved from an early-stage AngularJS codebase into a scalable Next.js-driven product ecosystem.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {subsections.map((item) => (
          <SubsectionCard key={item.id} item={item} onClick={() => setOpenSheet(item.id)} />
        ))}
      </div>

      <Sheet open={!!openSheet} onOpenChange={(open) => { if (!open) setOpenSheet(null); }}>
        <SheetContent
          side="right"
          className="!w-full sm:!w-[85vw] sm:!max-w-3xl bg-[#0A0A0A] border-l border-white/[0.06] p-0"
          data-testid="zoftware-detail-sheet"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{activeItem?.title || "Case Study Detail"}</SheetTitle>
            <SheetDescription>ZoftwareHub case study detail</SheetDescription>
          </SheetHeader>
          {activeItem && (
            <ScrollArea className="h-full">
              <div className="p-6 sm:p-10">
                <span className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-3">
                  ZoftwareHub
                </span>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#EDEDED] tracking-tight mb-6">
                  {activeItem.title}
                </h2>
                <div className="text-[15px] text-[#A1A1AA] leading-relaxed space-y-4 mb-6">
                  {activeItem.narrative.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {activeItem.tags.map((t) => (
                    <span key={t} className="text-[11px] font-mono px-3 py-1 rounded-full border border-white/[0.08] text-[#A1A1AA] bg-white/[0.02]">{t}</span>
                  ))}
                </div>
                <ScreenshotRenderer data={activeItem} />
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};
